











































































































import { Component, Prop, Emit, PropSync } from 'nuxt-property-decorator'
import { cloneDeep, orderBy, reject, some } from 'lodash'
import {
  BODY_HEIGHT,
  Col,
  ColItem,
  DataTable,
  DataTableHeader,
  defaultSortColumn,
  SortColumn
} from '@/components/molecules/DataTable'
import { RecycleScroller } from '@/components/molecules/RecycleScroller'
import { SystemCode, SearchUser } from '@/api'
import { dataStore } from '@/utils/store-accessor'

export interface UserSearchTableItemData extends SearchUser {
  isSelected: boolean
}

export interface UserSearchTableItem {
  activeRowIndex: number
  scrollTop: number
  sortColumn: SortColumn
  items: UserSearchTableItemData[]
  sortedItems: () => UserSearchTableItemData[]
  selectedItem: () => UserSearchTableItemData
  select: (sortedIndex: number) => void
}

export const defaultUserSearchTableItem = (): UserSearchTableItem => ({
  activeRowIndex: -1,
  scrollTop: 0,
  sortColumn: defaultSortColumn(),
  items: [],
  sortedItems() {
    if (this.sortColumn.name !== '') {
      return orderBy<UserSearchTableItemData>(
        this.items,
        (item: UserSearchTableItemData) => {
          const propertyValue =
            item[this.sortColumn.name as keyof UserSearchTableItemData]
          const type: string = Object.prototype.toString
            .call(propertyValue)
            .slice(8, -1)
            .toLowerCase()

          if (this.sortColumn.name === 'studentGradeClass') {
            return item.studentGrade.shortName + item.studentClass.shortName
          }

          if (type === 'object') {
            return (propertyValue as SystemCode).shortName
          }

          return propertyValue
        },
        this.sortColumn.order as 'asc' | 'desc'[]
      )
    }

    return this.items
  },
  selectedItem() {
    // 状況的に選択データが存在しない状況はありえない
    return this.items.find(item => item.isSelected) as UserSearchTableItemData
  },
  select(sortedIndex: number) {
    this.sortedItems().forEach(
      (item, index) => (item.isSelected = index === sortedIndex)
    )
  }
})

@Component({
  components: {
    DataTableHeader,
    RecycleScroller
  }
})
export default class UserSearchTable extends DataTable {
  @PropSync('item', {
    type: Object,
    required: true
  })
  tableItem!: UserSearchTableItem

  @Prop({
    type: Boolean,
    default: false
  })
  selectable!: boolean

  @Prop({
    type: Number,
    default: BODY_HEIGHT
  })
  height!: number

  get enabledSchool(): boolean {
    return dataStore.enabledSchool
  }

  get displayPhone(): boolean {
    return dataStore.displayUserSearchPagePhone
  }

  get displayAddress(): boolean {
    return dataStore.displayUserSearchPageAddress
  }

  get displayMailAddress(): boolean {
    return dataStore.displayUserSearchPageMailAddress
  }

  get allCols(): Col[] {
    return [
      {
        width: 26,
        items: [
          {
            name: 'radio',
            align: 'center'
          }
        ]
      },
      {
        width: 50,
        items: [
          {
            name: 'no',
            align: 'right'
          }
        ]
      },
      {
        width: '13',
        resizable: true,
        movable: true,
        items: [
          {
            name: 'name',
            headerText: '氏名',
            sortable: true,
            sortOrder:
              this.tableItem.sortColumn.name === 'name'
                ? this.tableItem.sortColumn.order
                : ''
          }
        ]
      },
      {
        width: '8',
        resizable: true,
        movable: true,
        items: [
          {
            name: 'id',
            headerText: '利用者番号',
            sortable: true,
            sortOrder:
              this.tableItem.sortColumn.name === 'id'
                ? this.tableItem.sortColumn.order
                : ''
          }
        ]
      },
      {
        width: '6',
        resizable: true,
        movable: true,
        items: [
          {
            name: 'studentGradeClass',
            headerText: '年組',
            sortable: true,
            sortOrder:
              this.tableItem.sortColumn.name === 'studentGradeClass'
                ? this.tableItem.sortColumn.order
                : ''
          }
        ]
      },
      {
        width: '8',
        resizable: true,
        movable: true,
        items: [
          {
            name: 'studentNumber',
            headerText: '出席番号',
            sortable: true,
            sortOrder:
              this.tableItem.sortColumn.name === 'studentNumber'
                ? this.tableItem.sortColumn.order
                : ''
          }
        ]
      },
      {
        width: '7',
        resizable: true,
        movable: true,
        items: [
          {
            name: 'birthday',
            headerText: '生年月日',
            sortable: true,
            sortOrder:
              this.tableItem.sortColumn.name === 'birthday'
                ? this.tableItem.sortColumn.order
                : ''
          }
        ]
      },
      {
        width: '8',
        resizable: true,
        movable: true,
        items: [
          {
            name: 'phoneNumber',
            headerText: '電話番号',
            sortable: true,
            sortOrder:
              this.tableItem.sortColumn.name === 'phoneNumber'
                ? this.tableItem.sortColumn.order
                : ''
          }
        ]
      },
      {
        width: '14',
        resizable: true,
        movable: true,
        items: [
          {
            name: 'address',
            headerText: '住所',
            sortable: true,
            sortOrder:
              this.tableItem.sortColumn.name === 'address'
                ? this.tableItem.sortColumn.order
                : ''
          }
        ]
      },
      {
        width: '16',
        resizable: true,
        movable: true,
        items: [
          {
            name: 'mailAddress',
            headerText: 'メールアドレス',
            sortable: true,
            sortOrder:
              this.tableItem.sortColumn.name === 'mailAddress'
                ? this.tableItem.sortColumn.order
                : ''
          }
        ]
      },
      {
        width: '5',
        resizable: true,
        movable: true,
        items: [
          {
            name: 'userType',
            headerText: '区分',
            sortable: true,
            sortOrder:
              this.tableItem.sortColumn.name === 'userType'
                ? this.tableItem.sortColumn.order
                : ''
          }
        ]
      },
      {
        width: '5',
        resizable: true,
        movable: true,
        items: [
          {
            name: 'lendCount',
            headerText: '貸出',
            align: 'right',
            sortable: true,
            sortOrder:
              this.tableItem.sortColumn.name === 'lendCount'
                ? this.tableItem.sortColumn.order
                : ''
          }
        ]
      },
      {
        width: '5',
        resizable: true,
        movable: true,
        items: [
          {
            name: 'reserveCount',
            headerText: '予約',
            align: 'right',
            sortable: true,
            sortOrder:
              this.tableItem.sortColumn.name === 'reserveCount'
                ? this.tableItem.sortColumn.order
                : ''
          }
        ]
      },
      {
        width: '5',
        resizable: true,
        movable: true,
        items: [
          {
            name: 'status',
            headerText: '状態',
            sortable: true,
            sortOrder:
              this.tableItem.sortColumn.name === 'status'
                ? this.tableItem.sortColumn.order
                : ''
          }
        ]
      }
    ]
  }

  get showCols(): Col[] {
    let cols: Col[] = cloneDeep(this.allCols)
    const deleteTargetCols: { enabled: boolean; name: string }[] = [
      { enabled: this.enabledSchool, name: 'studentGradeClass' },
      { enabled: this.enabledSchool, name: 'studentNumber' },
      { enabled: this.displayPhone, name: 'phoneNumber' },
      { enabled: this.displayAddress, name: 'address' },
      { enabled: this.displayMailAddress, name: 'mailAddress' }
    ]
    let totalWidth: number = 0

    deleteTargetCols.forEach(({ enabled, name }) => {
      if (!enabled) {
        const findCol: Col | undefined = cols.find(
          col => col.items.length > 0 && col.items[0].name === name
        )

        if (findCol) {
          cols = cols.filter(col => col.items.some(col => col.name !== name))
          totalWidth += Number(findCol.width)
        }
      }
    })

    while (totalWidth > 0) {
      cols.forEach(col => {
        if (typeof col.width === 'number' || totalWidth === 0) {
          return
        }

        col.width = String(Number(col.width) + 1)
        totalWidth -= 1
      })
    }

    if (this.selectable) {
      return cols
    } else {
      return reject<Col>(cols, col =>
        some<ColItem>(col.items, ['name', 'radio'])
      )
    }
  }

  isOrganization(code: string): boolean {
    return code.startsWith('8')
  }

  userIcon(userType: SystemCode): string {
    return this.isOrganization(userType.code) ? 'users' : 'male'
  }

  userIconState(status: SystemCode): string {
    return status.code === '1' ? 'male' : 'invalid'
  }

  onTableSort(sortColumn: SortColumn) {
    this.tableItem.sortColumn = sortColumn
  }

  onClickTableRow(index: number): void {
    if (this.selectable && this.tableItem.items.length) {
      this.tableItem.select(index)
    }
    this.onClickRow(index)
  }

  @Emit('link')
  onClickLink(index: number): number {
    this.tableItem.select(index)
    return index
  }
}
